import {
  CUSTOM_TAG_COLOR_PALETTE,
  SUBJECT_TAG_COLORS,
} from "../data/myNotesSubjectColors";

const stripHtml = (value = "") =>
  value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

export const formatTagLabel = (tag) => {
  if (!tag || typeof tag !== "string") {
    return tag;
  }
  return tag
    .split("-")
    .map((segment) =>
      segment
        .split(/([/ ]+)/)
        .map((part, i) =>
          i % 2 === 0
            ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            : part,
        )
        .join(""),
    )
    .join(" ");
};

/** Shorter on-chip labels; filter/save value stays the raw `tag`. */
const TAG_DISPLAY_SHORT = {
  "general-upsc-preparation-strategy": "UPSC prep & strategy",
  "ethics-integrity": "Ethics & integrity",
  "intl-relations": "Intl relations",
  "disaster-management": "Disaster management",
  "indian-society": "Indian society",
  "social-justice": "Social justice",
  "environment-disaster-management": "Environment & disaster",
  "society-social-justice": "Society & social justice",
  "science-tech": "Science & tech",
  "internal-security": "Internal security",
  "international-relations": "Intl relations",
  "post-independence": "Post independence",
  "world-history": "World history",
  "modern-history": "Modern history",
};

export const getTagDisplayLabel = (tag) => TAG_DISPLAY_SHORT[tag] ?? formatTagLabel(tag);

const normalizeTagKey = (tag = "") =>
  String(tag).trim().toLowerCase().replace(/\s+/g, "-");

export const getSubjectColorScheme = (tag) => {
  const key = normalizeTagKey(tag);
  if (SUBJECT_TAG_COLORS[key]) {
    return SUBJECT_TAG_COLORS[key];
  }

  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }

  return CUSTOM_TAG_COLOR_PALETTE[Math.abs(hash) % CUSTOM_TAG_COLOR_PALETTE.length];
};

export const getSubjectAccentColor = (tag) => getSubjectColorScheme(tag).accent;

export const getSubjectTagStyle = (tag) => {
  const scheme = getSubjectColorScheme(tag);
  return {
    backgroundColor: scheme.bg,
    color: scheme.text,
    borderColor: scheme.accent,
  };
};

export const getNoteAccentColor = (note) =>
  getSubjectAccentColor(note?.tags?.[0] || "General");

export const getNoteAttachmentCount = (note) => (note?.attachments || []).length;

export const noteHasAttachments = (note) => getNoteAttachmentCount(note) > 0;

const getAttachmentFileName = (attachment) => {
  const name = attachment?.fileName || attachment?.file?.name || "";
  return String(name).trim();
};

/** First attachment file name (without extension) for use as note title. */
export const deriveTitleFromAttachments = (attachments = []) => {
  const first = (attachments || []).find((attachment) => getAttachmentFileName(attachment));
  if (!first) {
    return "";
  }

  const fileName = getAttachmentFileName(first);
  const withoutExtension = fileName.replace(/\.[^.]+$/, "");
  return (withoutExtension || fileName).trim();
};

/**
 * Use typed title, else content snippet, else first attachment file name, else fallback.
 * Third argument may be a fallback string (legacy) or `{ fallback, attachments }`.
 */
export const deriveNoteTitle = (title = "", content = "", third = {}) => {
  const options =
    typeof third === "string" ? { fallback: third } : third || {};
  const { fallback = "Untitled Note", attachments = [] } = options;

  const trimmedTitle = (title || "").trim();
  if (trimmedTitle) {
    return trimmedTitle;
  }

  const plainText = stripHtml(content);
  if (plainText) {
    const maxLength = 80;
    if (plainText.length <= maxLength) {
      return plainText;
    }
    return `${plainText.slice(0, maxLength).trim()}...`;
  }

  const fromAttachment = deriveTitleFromAttachments(attachments);
  if (fromAttachment) {
    return fromAttachment;
  }

  return fallback;
};

const escapeHtml = (value = "") =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** Title is left empty so the notes API can assign one (e.g. LLM-generated). */
export const createChatSelectionNoteDraft = ({
  selectedText = "",
  tag = "",
} = {}) => {
  const trimmedTag = typeof tag === "string" ? tag.trim() : "";
  const tags = trimmedTag ? [trimmedTag] : ["General"];

  return {
    title: "",
    content: `<p>${escapeHtml(selectedText).replace(/\n/g, "<br />")}</p>`,
    tags,
    attachments: [],
  };
};

export const createCurrentAffairsSelectionNoteDraft = ({
  title = "",
  selectedText = "",
} = {}) => ({
  title: stripHtml(title) || "Current Affairs",
  content: `<p>${escapeHtml(selectedText).replace(/\n/g, "<br />")}</p>`,
  tags: ["Current Affairs"],
  attachments: [],
});

export const buildNotePreview = (note, maxLength = 72) => {
  const plainText = stripHtml(note.content);

  if (plainText) {
    if (plainText.length <= maxLength) {
      return plainText;
    }

    return `${plainText.slice(0, maxLength).trim()}...`;
  }

  return note.preview || "";
};

const parseSectionDate = (label) => {
  if (label === "Today") {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  if (label === "Yesterday") {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  const parsed = new Date(label);
  if (!isNaN(parsed.getTime())) return parsed;
  return new Date(0);
};

export const groupNotesBySection = (notes) => {
  const sorted = [...notes].sort(
    (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
  );

  const grouped = sorted.reduce((accumulator, note) => {
    const sectionLabel = note.sectionLabel || "Other";
    if (!accumulator[sectionLabel]) {
      accumulator[sectionLabel] = [];
    }
    accumulator[sectionLabel].push(note);
    return accumulator;
  }, {});

  const today = grouped.Today ? [{ sectionLabel: "Today", notes: grouped.Today }] : [];
  const yesterday = grouped.Yesterday
    ? [{ sectionLabel: "Yesterday", notes: grouped.Yesterday }]
    : [];

  const remaining = Object.keys(grouped)
    .filter((label) => label !== "Today" && label !== "Yesterday")
    .sort((a, b) => parseSectionDate(b) - parseSectionDate(a))
    .map((sectionLabel) => ({
      sectionLabel,
      notes: grouped[sectionLabel],
    }));

  return [...today, ...yesterday, ...remaining];
};

export const filterNotes = (notes, { searchTerm = "", selectedTags = [] } = {}) => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const normalizedTags = selectedTags.map((tag) => tag.toLowerCase());

  return notes.filter((note) => {
    const searchableText = [
      note.title,
      buildNotePreview(note),
      stripHtml(note.content),
      ...(note.tags || []),
    ]
      .join(" ")
      .toLowerCase();

    const matchesSearch =
      !normalizedSearch || searchableText.includes(normalizedSearch);

    const matchesTags =
      normalizedTags.length === 0 ||
      normalizedTags.some((tag) =>
        (note.tags || []).some((noteTag) => noteTag.toLowerCase() === tag),
      );

    return matchesSearch && matchesTags;
  });
};

export { partitionNoteTags, mergeNoteTags } from "../data/myNotesSubjectTopics";

export const getUniqueTags = (notes) => {
  const tags = new Set();
  notes.forEach((note) => {
    (note.tags || []).forEach((tag) => tags.add(tag));
  });
  return Array.from(tags).sort((left, right) => left.localeCompare(right));
};

export const createEmptyNoteDraft = () => ({
  id: null,
  title: "",
  content: "<p></p>",
  tags: [],
  attachments: [],
  attachmentIdsToRemove: [],
  accentColor: "#4DA3FF",
  sectionLabel: "Today",
  updatedAt: new Date().toISOString(),
});
